import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router';
import { ApiError } from '../lib/api';
import { pushToast } from '../lib/events';
import { acceptInvite, invitePreviewQuery, roomQuery, roomsQuery } from '../lib/rooms';

/**
 * Preview → Accept → navigate to the room. `GET /api/invites/{token}` answers 404 for unknown,
 * expired, revoked, *and* exhausted invites — one answer, no oracle — so this page never says which
 * one it was, just "no longer valid".
 */
export function Join() {
  const params = useParams<{ token: string }>();
  const token = params.token ?? '';
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data, isPending, error } = useQuery({ ...invitePreviewQuery(token), enabled: token !== '' });

  const acceptMutation = useMutation({
    mutationFn: acceptInvite,
    onSuccess: ({ roomId }) => {
      void queryClient.invalidateQueries({ queryKey: roomQuery(roomId).queryKey });
      // Accepting adds this room to "my rooms" (or changes my role in it), so the Home list must
      // refetch too — not just the room-by-id key, which nothing but the room page itself observes.
      void queryClient.invalidateQueries({ queryKey: roomsQuery.queryKey });
      navigate(`/rooms/${roomId}`, { replace: true });
    },
    onError: () => pushToast('Could not accept this invite.'),
  });

  if (token === '' || isPending) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <p className="telemetry text-text-faint">Checking invite…</p>
      </div>
    );
  }

  if (error || !data) {
    const isGone = error instanceof ApiError && error.status === 404;
    return (
      <div className="mx-auto max-w-6xl">
        <span className="eyebrow reveal">Off air</span>
        <h1 className="display display-outline reveal mt-4 text-[clamp(4rem,14vw,9rem)]">Invite</h1>
        <p className="reveal mt-4 text-text-muted">
          {isGone ? 'This invite is no longer valid.' : 'Could not check this invite. Please retry in a moment.'}
        </p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl">
      <div className="reveal flex items-baseline gap-4" style={{ ['--i' as string]: 0 }}>
        <span className="eyebrow">Invite</span>
        <span className="h-px flex-1 bg-border" aria-hidden="true" />
      </div>
      <h1 className="display reveal mt-4 text-[clamp(3rem,8vw,6rem)]" style={{ ['--i' as string]: 1 }}>
        {data.roomTitle}
      </h1>
      <p className="reveal mt-4 text-lg text-text-muted" style={{ ['--i' as string]: 2 }}>
        You&apos;re invited to <strong className="text-text">{data.roomTitle}</strong> as{' '}
        <span className="eyebrow">{data.role}</span> by {data.hostUsername}.
      </p>
      <p className="reveal mt-2 text-sm text-text-faint" style={{ ['--i' as string]: 3 }}>
        {data.className} · {data.guestLabel}
      </p>
      <button
        type="button"
        disabled={acceptMutation.isPending}
        onClick={() => acceptMutation.mutate({ token })}
        className="reveal mt-8 min-h-tap rounded-md border border-accent-border bg-accent-dim px-6 text-base font-semibold text-accent disabled:cursor-not-allowed disabled:opacity-50"
        style={{ ['--i' as string]: 4 }}
      >
        Accept invite
      </button>
    </div>
  );
}
